import { useEffect, useState } from 'react';
import { Formik } from 'formik';
import { StepBlock } from '../../../components/StepBlock/StepBlock';
import { Button } from '../../../components/Button/Button';
import successIcon from 'assets/images/icons/success.svg';
import arrowPrev from 'assets/images/icons/arrow-prev.svg';
import checkIcon from 'assets/images/icons/check.svg';
import { CreatePersonPayload, Person } from 'services/person/person.types';
import { useSelector } from 'react-redux';
import { Datalist } from 'components/Datalist/Datalist';
import plus from 'assets/images/icons/plus.svg';
import { createNewPersonThirdStepSchema } from '../NewPersonFormsSchema';
import { getDatalists } from 'features/datalist';

interface NewPersonThirdStepProps {
    handleCreatePerson: (data: CreatePersonPayload) => void;
    handlePreviousStep: (data: CreatePersonPayload) => void;
    isSuccess: boolean;
    setActiveStep: (step: number) => void;
    person: Person;
}

export const NewPersonThirdStep: React.FC<NewPersonThirdStepProps> = ({
    handleCreatePerson,
    handlePreviousStep,
    isSuccess,
    setActiveStep,
    person
}) => {
    const { datalists } = useSelector(getDatalists);

    return (
        <>
            <Formik
                initialValues={{
                    firstname: person?.firstname || '',
                    lastname: person?.lastname || '',
                    phone: person?.phone || '',
                    disorders: person?.disorders || {
                        mdph: 'no',
                        main: '',
                        secondaries: []
                    }
                }}
                validationSchema={createNewPersonThirdStepSchema(datalists || {})}
                enableReinitialize={true}
                validateOnChange={false}
                validateOnBlur={true}
                onSubmit={async (values: CreatePersonPayload, { setSubmitting }) => {
                    handleCreatePerson(values);
                    setSubmitting(false);
                }}
            >
                {({ values, errors, handleChange, handleBlur, handleSubmit, setFieldValue }) => {
                    return (
                        <form onSubmit={handleSubmit} className="form">
                            <StepBlock title="Caractéristiques de la personne soutenue">
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Caractéristique principale</label>
                                        <Datalist
                                            list="disorders"
                                            name="disorders.main"
                                            placeholder="Sélectionner une caractéristique"
                                            onChange={(value) => {
                                                setFieldValue('disorders.main', value.target.value, false);
                                            }}
                                            onBlur={handleBlur}
                                            value={values.disorders?.main}
                                        />
                                        {typeof errors.disorders === 'object' && (errors.disorders as any).main && (
                                            <p className="error">{(errors.disorders as any).main}</p>
                                        )}
                                    </div>
                                </div>

                                {values.disorders?.secondaries.map((secondary, index) => (
                                    <div className="form-line" key={`secondary-${index}`}>
                                        <div className="form-group">
                                            <label>Caractéristique secondaire n°{index + 1}</label>
                                            <Datalist
                                                list="disorders"
                                                name={`disorders.secondaries.${index}`}
                                                placeholder="Sélectionner une caractéristique"
                                                onChange={(value) => {
                                                    const newSecondaries = [...(values.disorders?.secondaries || [])];
                                                    newSecondaries[index] = value.target.value;
                                                    setFieldValue('disorders.secondaries', newSecondaries);
                                                }}
                                                onBlur={handleBlur}
                                                value={secondary}
                                            />
                                            {errors.disorders?.[index] && (
                                                <p className="error">{(errors.disorders as any)[index].secondaries}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}

                                <div
                                    className="button light"
                                    onClick={() => {
                                        setFieldValue('disorders.secondaries', [...(values.disorders?.secondaries || []), ''], false);
                                    }}
                                >
                                    <img src={plus} alt="Ajouter une caractéristique supplémentaire" />
                                    Ajouter une caractéristique supplémentaire
                                </div>

                                <div
                                    className="form-line"
                                >
                                    <div className="form-group">
                                        <label>Reconnaissance MDPH</label>
                                        <select
                                            name="disorders.mdph"
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            value={values.disorders?.mdph}
                                        >
                                            <option value="yes">Oui</option>
                                            <option value="pending">En cours</option>
                                            <option value="no">Non</option>
                                        </select>
                                        {typeof errors.disorders === 'object' && (errors.disorders as any).mdph && (
                                            <p className="error">{(errors.disorders as any).mdph}</p>
                                        )}
                                    </div>
                                </div>
                            </StepBlock>
                            <div className="actions-buttons">
                                <Button
                                    type="button"
                                    label="Retour à l'étape précédente"
                                    icon={<img src={arrowPrev} alt="Retour à l'étape précédente" />}
                                    iconBefore
                                    onClick={(e) => {
                                        handlePreviousStep(values);
                                    }}
                                />
                                <Button
                                    type="submit"
                                    label="Enregistrer le dossier"
                                    icon={<img src={checkIcon} alt="Enregistrer le dossier" />}
                                    iconAfter
                                />
                            </div>
                        </form>
                    );
                }}
            </Formik>
            {isSuccess && (
                <div className="success">
                    <div className="success-block">
                        <p>Votre dossier est bien enregistré</p>
                        <img src={successIcon} alt="Dossier enregistré avec succès" />
                    </div>
                </div>
            )}
        </>
    );
};
